import qs from "qs";
import axios from "axios";
import { getToken, toNumber } from "./service";
import moment from "moment";

export function getHouOptions() {
    const arr = [];
    for (let i = 0; i < 24; i++) {
        arr.push(i);
    }
    return arr;
}

export function getMinOptions() {
    const arr = [];
    for (let i = 0; i < 60; i++) {
        arr.push(i);
    }
    return arr;
}

export enum BaseQuestionType {
    SingleChoice = 1,
    MultipleChoice = 2, // 多选
    Subjective = 4 // 主观
}

export const BaseQuestionTypeMap = {
    [BaseQuestionType.SingleChoice]: "单选题",
    [BaseQuestionType.MultipleChoice]: "多选题",
    [BaseQuestionType.Subjective]: "主观题"
};

export interface OptionsType {
    id?: string;
    key?: number;
    questionId?: string;
    description: string;
}

// 填空题选项
export interface BlankType {
    id?: string;
    description: string;
}

interface QuestionBase {
    id?: string; // 题ID (新增不传，修改传)
    paperId: string; // 练习id
    queTypeId?: BaseQuestionType; // 题类型1:单选题2:多选3:判断4:主观题5:填空题6阅读理解
    contentText: string; // 题干
    sort?: number;
    requiredFlag?: BooleanValue;
}

// 单选题型
export interface SingleChoiceType extends QuestionBase {
    queTypeId: BaseQuestionType.SingleChoice;
    options: OptionsType[];
}

// 多选题型
interface MultipleChoiceType extends QuestionBase {
    queTypeId: BaseQuestionType.MultipleChoice;
    options: OptionsType[];
}

// 主观题型
interface SubjectiveType extends QuestionBase {
    queTypeId: BaseQuestionType.Subjective;
    analysis: string; // 参考答案
}

// 其他题型
interface MixedType extends QuestionBase {
    queTypeId: number;
    questions: SimpleQuestionType[];
}

type SimpleQuestionType =
    | SingleChoiceType
    | MultipleChoiceType
    | SubjectiveType;
export type QuestionType = SimpleQuestionType | MixedType;

interface optionModelListItem {
    id: number; //选项id
    questionId: number; //题id
    description: string; //选项描述
}

interface questionModelListItem {
    id: number; //小题id
    queTypeId: number; //小题类型1:单选2:多选3:判断4:主观5:填空6:阅读理解7:以后都是自定义题型
    description: string; //小题的题目
    contentText: string; //原题目
    analysis: string; //主观题答案
    parentId: number; //父ID
    paperId: number; //活动ID
    paperType: number; //活动类型0:练习1:作业
    createUser: number; //创建人ID
    updateUser: null; //修改人ID
    createTime: string; //创建时间
    sort: string; //排序
    queTypeName: string; //题型名称
    optionModelList: optionModelListItem[];
}

export interface QuestionListItem {
    requiredFlag: BooleanValue;
    id: number; //大题ID
    queTypeId: number; //大题的类型
    description: string; //大题描述(题目)
    contentText: string; //原题目
    analysis: string; //主观题答案
    paperId: number; //活动id
    paperType: number; //活动类型0:练习1:作业
    createTime: string; //创建时间
    createUser: number; //创建人
    sort: string; //排序
    queTypeName: string; //题型名称
    optionModelList: optionModelListItem[];
    questionModelList: questionModelListItem[];
    index: number;
}

/**
 * 单选题类型守护
 * @param data
 */
export function isSingleChoiceType(
    data: QuestionType
): data is SingleChoiceType {
    return data.queTypeId === BaseQuestionType.SingleChoice;
}

/**
 * 多选题类型守护
 * @param data
 */
export function isMultipleChoiceType(
    data: QuestionType
): data is MultipleChoiceType {
    return data.queTypeId === BaseQuestionType.MultipleChoice;
}

/**
 * 主观题类型守护
 * @param data
 */
export function isSubjectiveType(data: QuestionType): data is SubjectiveType {
    return data.queTypeId === BaseQuestionType.Subjective;
}

/**
 *
 * @param questionnaireId 试题列表
 */
export function getQuestionList(questionnaireId: string) {
    return axios
        .post<SuccessData<QuestionListItem[]>>(
            `/teacher/questionnaire/getQuestionList`,
            `questionnaireId=${questionnaireId}`
        )
        .then(res => res.data.data)
        .then(data =>
            data.map((item: QuestionListItem, index: number) => ({
                ...item,
                index: index + 1
            }))
        );
}

/**
 * 把服务端数据转成前端试题类型
 * @param data
 */
export function convertQuestion(data: QuestionListItem): QuestionType {
    const queTypeId = data.queTypeId;

    const question: QuestionType = {
        id: String(data.id),
        contentText: data.contentText,
        requiredFlag: data.requiredFlag,
        options: [],
        queTypeId: data.queTypeId,
        paperId: String(data.paperId),
        sort: toNumber(data.sort)
    };

    if (
        [
            BaseQuestionType.SingleChoice,
            BaseQuestionType.MultipleChoice
        ].includes(queTypeId)
    ) {
        return { ...question, options: convertOptions(data.optionModelList) };
    }

    if (queTypeId === BaseQuestionType.Subjective) {
        return { ...question, analysis: data.analysis };
    }

    const questions = data.questionModelList.map(data =>
        convertQuestion(data as any)
    ) as SimpleQuestionType[];
    return { ...question, questions };
}

/**
 * 把前端试题格式转换成服务器格式
 */
function serializeQuestions(data: QuestionType, toJson = true) {
    const params: any = { ...data }; // 不直接修改data, 防止引起引用错误

    if ("options" in params) {
        if (toJson) params.options = JSON.stringify(params.options);
    }

    if ("judgeOptions" in params) {
        if (toJson) params.options = JSON.stringify(params.options);
        delete params.judgeOptions;
    }

    if ("blanks" in params) {
        params.options = params.blanks.map((option: any) => ({
            ...option,
            description: option.description
        }));
        if (toJson) params.options = JSON.stringify(params.options);
        delete params.blanks;
    }

    if ("questions" in params) {
        if (params.questions.length === 0) throw new Error("还没有添加小题!");
        if (toJson)
            params.questions = JSON.stringify(
                params.questions.map((data: QuestionType, index: number) => ({
                    ...serializeQuestions(data, false),
                    sort: index
                }))
            );
    }

    return params;
}

/**
 * 把服务端数据转成前端试题类型
 * @param type
 * @param options
 */
function convertOptions(options: optionModelListItem[]) {
    return options.map(item => ({
        ...item,
        key: item.id
    })) as unknown as OptionsType[];
}

/**
 * 添加或修改试题
 * @param data
 */
export function saveQuestion(data: any) {
    const params = serializeQuestions(data);
    return axios.post(
        "/teacher/questionnaire/saveQuestion",
        qs.stringify(params)
    );
}

export interface QuestionMainListItem {
    anonymityFlag: BooleanValue;
    description: string;
    endTime: string;
    id: number;
    isHaveQuestion: BooleanValue;
    noJoinInQuestionnaireCount: number;
    questionnaireAllCount: number;
    questionnaireCount: number;
    joinInQuestionnaireCount: number;
    questionnaireStatus: QuestionStatus;
    startTime: string;
    validityTime: number;
    weekTimeId: number;
}

/**
 * 获取练习活动列表数据
 * @param weekTimeId
 */
export function getQuestionnaireList(weekTimeId: number) {
    return axios
        .post<SuccessData<QuestionMainListItem[]>>(
            `/teacher/questionnaire/getQuestionnaireList`,
            qs.stringify({ weekTimeId })
        )
        .then(data => data.data.data);
}

export function timeToMin(d: number, h: number, m: number) {
    const D = d * 24 * 60;
    const H = h * 60;
    return D + H + m;
}

export interface EditQuestionParams {
    weekTimeId: number;
    description: string;
    anonymityFlag: BooleanValue; //是否匿名
    validityTime?: number; //限时时间
    id?: any;
    studentIds?: string[];
}

/**
 * 添加/修改练习
 * @param params
 */
export function saveQuestionnaire(params: EditQuestionParams) {
    return axios
        .post(
            `/teacher/questionnaire/saveQuestionnaire`,
            qs.stringify({ ...params })
        )
        .then(data => data.data);
}

/**
 * 设置活动返显
 * @param id
 */
export function getQuestionnaire(id: number) {
    return axios
        .post<SuccessData<EditQuestionParams>>(
            `/teacher/questionnaire/getQuestionnaire`,
            qs.stringify({ id })
        )
        .then(data => data.data.data);
}

export enum QuestionStatus {
    NoChild = -1, // 未添加题目
    NotStarted = 0, // 有题目未开始
    doing = 1, // 进行中
    done = 2, // 已结束
    del = 3 // 已删除
}

export interface TopicItem {
    id: string;
    description: string; //题型名称
    shareStatus: string; //1基本类型，0新增类型
}

export interface TopicItemWithSubTypes extends TopicItem {
    basicTypeIds: string;
}

/**
 * 题型管理列表
 */
export function getTopicTypeList() {
    return axios
        .post<SuccessData<TopicItemWithSubTypes[]>>(
            `/teacher/questionnaire/getTopicTypeList`
        )
        .then(res => res.data.data);
}

/**
 * 添加题型列表
 */
export function getAddTopicTypeList() {
    return axios
        .post<SuccessData<TopicItem[]>>(
            `/teacher/questionnaire/getAddTopicTypeList`
        )
        .then(res => res.data.data);
}

export interface TopicTypeProp {
    id: string; //题型ID
    description: string; //题型名称
    extendStatus: string; //基本题型:0不是1:是
    basicTypeIds: string; //拥有小题的题型
    shareStatus: string; //默认题型0:不是默认的1:默认的
    createUser: string;
    updateUser: string;
    createTime: string;
    updateTime: string;
    isHaveQuestion: string; //题型下面是否有题0:没有1:有
}

/**
 * 编辑自定义题型（反显）
 *
 */
export function getTopicType(topicTypeId: string) {
    return axios
        .post<SuccessData<TopicTypeProp>>(
            `/teacher/questionnaire/getTopicType`,
            qs.stringify({ topicTypeId })
        )
        .then(res => res.data.data);
}

/**
 * 添加/修改练习
 * @param description 题型名称
 * @param basicTypeIds 添加的题型id 多个用逗号拼接
 * @param id(新增不传修改传)
 */
export function saveTopicType(
    id: string,
    description: string,
    basicTypeIds: string
) {
    return axios
        .post(
            `/teacher/questionnaire/saveTopicType`,
            qs.stringify({ basicTypeIds, description, id })
        )
        .then(res => res.data.data);
}

/**
 * 顺序修改保存
 * @param topicTypeIds 示例8,4,3,2,1,7,6,5 多个题型ID
 */
export function managerTopicTypeSort(topicTypeIds: string) {
    return axios
        .post(
            `/teacher/questionnaire/managerTopicTypeSort`,
            qs.stringify({ topicTypeIds })
        )
        .then(res => res.data.data);
}

export interface CourseInfoItem {
    id: number; //课程id
    customerCode: string; //租户code
    batchNo: string; //学期
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    courseName: string; //课程名称
    teacherId: number; //教师ID
}

/**
 * 课程信息下拉列表
 */

export function getQuestionCourseInfo() {
    return axios
        .post<SuccessData<CourseInfoItem[]>>(
            `/teacher/questionnaire/getQuestionCourseInfo`
        )
        .then(res => res.data.data);
}

export interface QuestionBaseList {
    rowNo: number;
    questionId: number; //试题ID
    batchName: string; //学期
    courseName: string; //课程名称
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    queTypeDescription: string; //题型
    questionDescription: string; //题目
    paperType: string; //试卷类别:0：练习1:作业
    createTime: string; //创建时间
    updateTime: string; //修改时间
    questionnaireId: number; //活动ID
}

export interface QuestionDatabaseItem {
    allCount: number;
    list: QuestionBaseList[];
}

interface QuestionParams {
    search: string;
    pageSize: number;
    currPage: number;
    questionnaireId: number;
    sortName: string;
    sortType: string;
    courseId: string;
    queTypeId: string;
}

/**
 * 题库列表
 *
 * @param params
 */

export function getQuestionDatabaseList(params: QuestionParams) {
    const token = getToken();
    return axios
        .post<SuccessData<QuestionDatabaseItem>>(
            `/teacher/questionnaire/getQuestionDatabaseList`,
            qs.stringify(params),
            { headers: { token } }
        )
        .then(res => res.data.data);
}

export function copyQuestion(questions: string, questionnaireId: string) {
    const token = getToken();
    return axios.post(
        `/teacher/questionnaire/copyQuestion`,
        qs.stringify({ questions, questionnaireId }),
        { headers: { token } }
    );
}

/**
 * 试题排序 大题
 */

export function saveBigQuestionSort(
    questionnaireId: string,
    questionIds: string
) {
    const token = getToken();
    return axios.post(
        `/teacher/questionnaire/saveBigQuestionSort`,
        qs.stringify({ questionnaireId, questionIds }),
        { headers: { token } }
    );
}

/**
 * 删除当前题
 */
export function removeQuestion(questionnaireId: string, questionId: string) {
    const token = getToken();
    return axios.post(
        `/teacher/questionnaire/removeQuestion`,
        qs.stringify({ questionnaireId, questionId }),
        { headers: { token } }
    );
}
/**
 * 开始练习
 * @param weekTimeId
 * @param questionnaireId
 */
export function publishQuestionnaire(
    weekTimeId: number,
    questionnaireId: number
) {
    return axios
        .post(
            `/teacher/questionnaire/publishQuestionnaire`,
            qs.stringify({ weekTimeId, questionnaireId })
        )
        .then(data => data.data.data);
}

export interface QuestionnaireInfo {
    questionnaireId: number;
    questionnaireName: string;
    validityTime: any;
    startTime: string;
    endTime: string;
    createTime: string;
    useTime: string;
    questionModels: ExerciseQuestionItem[];
    questionnaireStatus: QuestionStatus;
    anonymityFlag: BooleanValue; //匿名
}

export interface ExerciseQuestionItem {
    id: number;
    queTypeId: BaseQuestionType;
    description: string;
    paperId: number;
    paperType: number;
    queTypeName: string;
    contentText: string;
}

/***
 * 开启活动后的统计(答题卡和活动信息)
 * @param data
 */
export function getQuestionInfo(data: { questionnaireId: number }) {
    return axios
        .post<SuccessData<QuestionnaireInfo>>(
            `/teacher/questionnaire/getQuestionnaireInfo`,
            qs.stringify(data)
        )
        .then(data => data.data.data);
}

export interface StuInfo {
    studentId: string;
    stuStatus: string;
    name: string;
}

export interface StuJoinInfo {
    courseCount: number;
    joinInCount: number;
    noJoinInCount: number;
    list: StuInfo[];
}

/**
 * 进行中的活动实时参与信息
 * @param questionnaireId
 * @param courseId
 */
export function getStuJoinInQuestionnaireList(
    questionnaireId: number,
    courseId: string
) {
    return axios
        .post<SuccessData<StuJoinInfo>>(
            `/teacher/questionnaire/getStuJoinInQuestionnaireList`,
            qs.stringify({ questionnaireId, courseId })
        )
        .then(data => data.data.data);
}

export interface CopyParams {
    questionnaireId: string;
    search: string;
    courseId: string;
    sortType: string;
    pageSize: number;
    currPage: number;
}

export interface CopyListItem {
    rowNo: number;
    questionnaireId: number;
    batchNo: string;
    customerCode: string;
    courseId: number;
    courseNo: string;
    courseSeq: string;
    courseName: string;
    exerciseName: string;
    weekTimeInfo: string;
    weekTimeId: number;
    questionCount: number;
    createTime: string;
    createUser: number;
}

export interface CopyList {
    allCount: number;
    list: CopyListItem[];
}

/**
 * 复制练习列表
 */

export function getCopyQuestionnaireList(params: CopyParams) {
    return axios
        .post<SuccessData<CopyList>>(
            `/teacher/questionnaire/getCopyQuestionnaireList`,
            qs.stringify(params)
        )
        .then(res => res.data.data);
}

/**
 * 复制练习提交
 */
export function copyQuestionnaire(
    currQuestionnaireId: string,
    copyQuestionnaireIds: string | number
) {
    return axios
        .post<SuccessData<{ data: number }>>(
            `/teacher/questionnaire/copyQuestionnaire`,
            qs.stringify({ currQuestionnaireId, copyQuestionnaireIds })
        )
        .then(res => res.data.data);
}

interface QuestionnaireDescribe {
    validityTime: string;
    description: string;
    startTime: string;
    endTime: string;
}

export interface ListsItem {
    submitTime: string; // 提交时间
    name: string; // 学生姓名
    index: string; // 序号
    className: string; // 班级
    studentNo: string; // 学生学号
    joinStatus: string; // 参与状态 1:已参与, 0:未参与
}

interface StudentListItem {
    joinPercent: string;
    questionnaire: QuestionnaireDescribe;
    studentNo: string;
    list: ListsItem[];
    joinNo: string;
    unJoinNo: string;
}
/**
 * 批阅列表
 */
export function getStudentList(questionnaireId: number) {
    return axios
        .get<SuccessData<StudentListItem>>(
            `/teacher/questionnaire/getMarkList?questionnaireId=${questionnaireId}`
        )
        .then(res => res.data.data);
}

/**
 * 收卷
 * @param questionnaireId
 * @param studentNo
 * @param courseId
 */
export function collectExaminationPaper(
    questionnaireId: string,
    studentNo: string,
    courseId: string
) {
    return axios.post(
        "/teacher/questionnaire/collectExaminationPaper",
        qs.stringify({ questionnaireId, studentNo, courseId })
    );
}

export interface OptionsItem {
    questionId: number;
    description: string;
    id: number;
    serialNum: string;
}

export interface viewPaperListItem {
    style: string; // 题目是基础题型还是多题干 1:基础题型(单题干), 2:多题干
    questions: viewPaperListItem[];
    id: number;
    sort: number;
    stuFinishTopic: string; // 学生本题是否作答 1:本题已作答完成, 0:本题未作答
    queTypeId: number;
    paperId: string;
    contentText: string;
    stuAns: string;
    analysis: string;
    teacherMark: string;
    stuIsRight: string;
    typeName: string;
    options: OptionsItem[];
    index: number;
    teacherComment: string;
    key: string;
}

export interface viewPaperObj {
    stuDoFlg: string; //学生试卷是否作答标识
    unfinishedCount: number; //学生未作答试题数
    topicCount: string; //试题数
    stuSubmitTime: string; //提交时间
    topics: viewPaperListItem[]; //试题列表
}

//获取题目正确答案
export function getRightOption(queTypeId: number, options: any[]) {
    //单选，多选题正确答案：选项（A,B....)
    if (
        [
            BaseQuestionType.SingleChoice,
            BaseQuestionType.MultipleChoice
        ].includes(queTypeId)
    ) {
        return options.map(item => item.serialNum);
    }
}

// 获得学生答案
export function getStuAnswer(
    queTypeId: number,
    stuAns: any,
    options: OptionsItem[]
) {
    //单/多选
    if (
        [
            BaseQuestionType.SingleChoice,
            BaseQuestionType.MultipleChoice
        ].includes(queTypeId)
    ) {
        return options
            .filter((item: OptionsItem) => stuAns.includes(item.id + ""))
            .map(item => item.serialNum)
            .join();
    }
}

/**
 * 打开学生试卷
 */
export function viewPaper(questionnaireId: number, studentNo: string) {
    const token = getToken();
    return axios
        .get<SuccessData<viewPaperObj>>(
            `/teacher/questionnaire/viewPaper?questionnaireId=${questionnaireId}&studentNo=${studentNo}`,
            { headers: { token } }
        )
        .then(res => res.data.data)
        .then(data => ({
            ...data,
            topics: data?.topics
                ? data.topics.map((item: viewPaperListItem, index: number) => ({
                      ...item,
                      index: index + 1,
                      key: studentNo + item.id,
                      options: item?.options
                          ? item.options.map((option, index) => ({
                                ...option,
                                serialNum: String.fromCharCode(index + 65)
                            }))
                          : [],
                      questions: item?.questions
                          ? item.questions.map(
                                (item: viewPaperListItem, index: number) => ({
                                    ...item,
                                    index: index + 1,
                                    options: item?.options
                                        ? item.options.map((option, index) => ({
                                              ...option,
                                              serialNum: String.fromCharCode(
                                                  index + 65
                                              )
                                          }))
                                        : []
                                })
                            )
                          : []
                  }))
                : []
        }));
}

/**
 * 结束练习
 * @param questionnaireId
 * @param weekTimeId
 */
export function endQuestionnaire(weekTimeId: number, questionnaireId: number) {
    return axios.post(
        `/teacher/questionnaire/endQuestionnaire`,
        qs.stringify({ questionnaireId, weekTimeId })
    );
}

/**
 * 删除练习
 * @param questionnaireId
 */
export function removeQuestionnaire(questionnaireId: number) {
    return axios.post(
        `/teacher/questionnaire/removeQuestionnaire`,
        qs.stringify({ questionnaireId })
    );
}

export interface EndExerciseStuInfo {
    studentId: string; // 学号
    stuName: string; // 姓名
}

export interface EndExerciseOptionModel {
    id: number; // 选项ID
    questionId: number; // 题ID
    description: string; // 选项描述
    stuOptionInfos?: EndExerciseStuInfo[]; // 选择选项的学生信息
}

export interface SubAnsItem {
    answer: string;
    name: string;
    studentNo: string;
    submitTime: string;
}
export interface EndExerciseQuestion {
    id: number;
    paperId: number;
    paperType: number;
    queTypeId: number;
    queTypeName: string;
    description: string;
    contentText: string;
    analysis: string;
    optionModelList: EndExerciseOptionModel[];
    questionModelList: EndExerciseQuestion[];
    accuracy: string; // 正确率
    stuRightList?: EndExerciseStuInfo[];
    stuUnRightList?: EndExerciseStuInfo[];
    stuAnswer: SubAnsItem[];
}

interface EndQuestionnaireInfo {
    joinPercent: string;
    stuCount: number; // 学生总数
    questionList: EndExerciseQuestion[];
}

/**
 * 练习结束详情统计
 * @param questionnaireId
 */
export function getEndQuestionnaireInfo(questionnaireId: number) {
    return axios
        .post<SuccessData<EndQuestionnaireInfo>>(
            "/teacher/questionnaire/getEndQuestionnaireInfo",
            qs.stringify({ questionnaireId })
        )
        .then(res => res.data.data);
}

export interface ExerciseStudentItem {
    studentId: string;
    flag: "1" | "0"; // 是否参加 0否，1是
    name: string;
}

/**
 * 重新发布选择列表
 * @param id
 * @param weekTimeId
 */
export function anewQuestionnaireStudentList(id: number, weekTimeId: number) {
    return axios
        .post<SuccessData<ExerciseStudentItem[]>>(
            "/teacher/questionnaire/anewQuestionnaireStudentList",
            qs.stringify({ id, weekTimeId })
        )
        .then(res => res.data.data);
}

/**
 * 重新发布
 * @param data
 */
export function anewUpdateQuestionnaire(data: {
    id: any;
    weekTimeId: number;
    validityTime: number;
    studentIds: string[];
}) {
    const { studentIds, ...formData } = data;

    return axios.post(
        "/teacher/questionnaire/anewUpdateQuestionnaire",
        qs.stringify({ ...formData, studentIds: studentIds?.join(",") })
    );
}

export interface QuestionPublishResume {
    validityTime: number;
    startTime: string;
    endTime: string;
}

//分钟转换为天数
export function convertMinutesToDHM(minutes: number) {
    const days = Math.floor(minutes / 1440); // 一天有1440分钟
    const hours = Math.floor((minutes % 1440) / 60); // 一小时有60分钟
    const mine = minutes % 60;

    return { days, hours, mine };
}
//分钟转换为天数TEXT
export function convertMinutesToDHMText(
    minutes: number | string | null | undefined
) {
    if (minutes === "不限") {
        return minutes;
    }
    if (!minutes) {
        return "不限";
    }
    const { days, hours, mine } = convertMinutesToDHM(Number(minutes));

    let result = "";
    if (days > 0) {
        result += days + "天";
    }
    if (hours > 0) {
        result += hours + "小时";
    }
    if (mine > 0) {
        result += mine + "分钟";
    }

    return result;
}

function countdown(endDate: string) {
    // 将目标日期和当前日期转换为 Moment 对象
    const end = moment(endDate);
    const now = moment();

    // 计算剩余时间
    const duration = moment.duration(end.diff(now));

    if (duration.asMilliseconds() <= 0) {
        return "已截止";
    }

    // 提取剩余时间的各个部分
    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    return (
        days + " 天 " + hours + " 小时 " + minutes + " 分钟 " + seconds + " 秒"
    );
}

/**
 * 发布履历
 * @param id
 */
export function questionnairePublishResume(id: number) {
    return axios
        .get<SuccessData<QuestionMainListItem[]>>(
            `/teacher/questionnaire/questionnairePublishResume?id=${id}`
        )
        .then(res => res.data.data);
}
